import React, { useState, useEffect, useCallback } from 'react'
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import { isMobile } from 'lib/utils/UiUtils';

const LineGraph = (props) => {
    const chartRef = React.useRef();
    let chart = null;

    const data = props.data;
    const options = props.options;
    const mobile = isMobile();

    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    const gradientColors = props.gradientColors;

    const widthBreakpoints = [
        {
            width: 400,
            fontSize: 10
        },
        {
            width: 800,
            fontSize: 12
        },
        {
            width: 1100,
            fontSize: 14
        },
    ]

    let resizeTimeoutId;
    const handleResize = () => {
        if (resizeTimeoutId) {
            window.clearTimeout(resizeTimeoutId);
        }
        resizeTimeoutId = window.setTimeout(() => {
            setScreenWidth(window.innerWidth);
            updateChart();
            resizeTimeoutId = false;
        }, 50);
    }

    const setFontSize = () => {
        let size = 16;

        for (let breakpoint of widthBreakpoints) {
            if (screenWidth < breakpoint.width) {
                size = breakpoint.fontSize;
                break;
            }
        }

        Chart.defaults.font.size = size;
        if (chart && chart.canvas) {
            chart.update();
        }
    }

    useEffect(() => {
        Chart.register(...registerables);
        Chart.register(annotationPlugin);

        // https://www.chartjs.org/docs/latest/axes/styling.html#grid-line-configuration

        Chart.defaults.elements.bar.borderRadius = mobile ? 5 : 10;
        Chart.defaults.elements.bar.pointRadius = mobile ? 5 : 10;
        Chart.defaults.elements.bar.barThickness = mobile ? 5 : 10;
        Chart.defaults.scale.ticks.padding = mobile ? 10 : 20;

        // Chart.defaults.borderColor = 'rgba(255,255,255,.4)';
        Chart.defaults.color = 'rgba(255,255,255,1)';
        Chart.defaults.font.family = 'Grotesk';
        setFontSize();

        try {
            let ctx = chartRef.current.getContext('2d');
            chart = new Chart(ctx, { type: 'line', data: data, plugins: [ChartDataLabels], options: options });
        } catch(err) {
            console.log(err);
        }

        return () => {
            if (chart && chart.canvas) {
                chart.destroy();
            }
        }

    }, [data]);

    const updateData = (d) => {
        try {
            const scaleId = d.yAxisID;

            const maxScale = chart.scales[scaleId].max;
            const minScale = chart.scales[scaleId].min;

            const maxData = d.data.length ? Math.max(...d.data) : 0;
            const maxHeight = chart.scales[scaleId].maxHeight;

            const yStart = maxHeight - (maxHeight * ((maxData - minScale) / (maxScale - minScale))) || 0;

            if (chart.ctx) {
                const gradient = chart.ctx.createLinearGradient(0, yStart, 0, maxHeight);
                gradient.addColorStop(0, gradientColors[scaleId][0]);
                gradient.addColorStop(1, gradientColors[scaleId][1]);

                d.backgroundColor = gradient;
            }
        } catch(error) {
            console.log(error);
        }
    }

    const updateChart = () => {
        if (chart && chart.canvas) {
            chart.data.datasets.forEach((d) => {
                updateData(d);
            })

            chart.update();
        }
    }

    useEffect(() => {
        if (chart && chart.canvas) {
            updateChart();
        }
    }, [chart, data]);

    useEffect(() => {
        let isMounted = true;
        const _handleResize = () => {
            if (isMounted) {
                handleResize();
            }
        }

        if (isMounted) {
            handleResize();
            window.addEventListener('resize', _handleResize);
        }
        return () => {
            isMounted = false;
            window.removeEventListener('resize', _handleResize);
            window.clearTimeout(resizeTimeoutId);
        }
    }, [])

    useEffect(() => {
        setFontSize();
    }, [screenWidth])

    return (
        <div className="line-graph-container">
            <canvas ref={chartRef} />
        </div>
    )
}

export default LineGraph;